import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import { useAuth } from '../context/AuthContext';
import './EODReport.css';

function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(v) {
  if (!v) return '-';
  try {
    return new Date(v).toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return v;
  }
}

const EMPTY_FILTERS = {
  prop_id: '', agent_id: '', program_name: '', confirm_status: '',
  date_from: '', date_to: '', matched: '', eod_file_date: '',
};

export default function EODReport() {
  const { can } = useAuth();
  const canUpload = can('eod-report', 'edit');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [agents, setAgents] = useState([]);
  const [totals, setTotals] = useState(null);
  const [transferRate, setTransferRate] = useState(0.6);

  // Upload state
  const [file, setFile] = useState(null);
  const [fileDate, setFileDate] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState('');

  const fetchRows = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(pagination.limit) });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const resp = await axios.get(`${API_BASE_URL}/api/apfs/eod?${params.toString()}`);
      if (resp.data.success) {
        setRows(resp.data.data || []);
        setPagination((p) => ({ ...p, ...resp.data.pagination }));
        setTotals(resp.data.totals || null);
        if (resp.data.transferRate != null) setTransferRate(resp.data.transferRate);
      } else {
        setError(resp.data.error || 'โหลดข้อมูลไม่สำเร็จ');
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  const fetchAgents = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_BASE_URL}/api/apfs/eod/agents`);
      if (resp.data.success) setAgents(resp.data.agents || []);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { fetchRows(1); fetchAgents(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onFilterChange = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => { setFilters(EMPTY_FILTERS); };

  const handleUpload = async () => {
    if (!file) { setUploadError('กรุณาเลือกไฟล์ EOD (.xlsx)'); return; }
    setUploading(true);
    setUploadError('');
    setUploadResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      if (fileDate) form.append('eod_file_date', fileDate);
      const resp = await axios.post(`${API_BASE_URL}/api/apfs/eod/import`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (resp.data.success) {
        setUploadResult(resp.data.summary);
        setFile(null);
        document.getElementById('eod-file-input').value = '';
        fetchRows(1);
        fetchAgents();
      } else {
        setUploadError(resp.data.error || 'นำเข้าไม่สำเร็จ');
      }
    } catch (e) {
      setUploadError(e.response?.data?.error || e.message);
    } finally {
      setUploading(false);
    }
  };

  const paidCount = rows.filter((r) => r.apfs_matched).length;
  const transferPct = Math.round((transferRate || 0.6) * 100);

  return (
    <div className="eod-report">
      <h2>EOD Confirmed Payment Report</h2>
      <p className="eod-subtitle">
        นำเข้าไฟล์ EOD รายวันจาก APFS/FIN แล้วจับคู่กับ APFS Submitted ด้วย Prop ID —
        แถวที่จับคู่ได้จะแสดง <span className="eod-badge eod-badge-paid">Paid</span>
      </p>

      {/* Upload — only for users with EOD edit permission */}
      {canUpload && (
      <div className="eod-upload">
        <div className="eod-upload-row">
          <div className="eod-upload-field">
            <label>ไฟล์ EOD (.xlsx / .csv)</label>
            <input
              id="eod-file-input"
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => { setFile(e.target.files[0] || null); setUploadError(''); }}
            />
          </div>
          <div className="eod-upload-field">
            <label>วันที่ของไฟล์ (EOD date)</label>
            <input type="date" value={fileDate} onChange={(e) => setFileDate(e.target.value)} />
          </div>
          <button className="eod-btn-primary" onClick={handleUpload} disabled={uploading}>
            {uploading ? 'กำลังนำเข้า...' : 'นำเข้า EOD'}
          </button>
        </div>
        {uploadError && <div className="eod-error"><span>⚠️</span>{uploadError}</div>}
        {uploadResult && (
          <div className="eod-upload-summary">
            ✅ นำเข้า <b>{uploadResult.imported}</b> แถว จากไฟล์ <b>{uploadResult.fileName}</b>
            {' '}— จับคู่ APFS ได้ <b className="text-green">{uploadResult.matchedToApfs}</b>,
            {' '}ไม่พบคู่ <b>{uploadResult.unmatched}</b>
            {uploadResult.skipped ? <>, ข้าม <b>{uploadResult.skipped}</b> (ไม่มี Prop_Id)</> : null}
            <div style={{ marginTop: 6 }}>
              💰 เบี้ยที่ได้รับจากไฟล์นี้: Gross <b>฿{fmtMoney(uploadResult.grossPremium)}</b>,
              {' '}After Discount <b>฿{fmtMoney(uploadResult.afterDiscount)}</b>,
              {' '}Transfer (60%) <b className="text-green">฿{fmtMoney(uploadResult.transfer)}</b>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Filters */}
      <div className="eod-filters">
        <div className="eod-filter-group">
          <label>Prop ID</label>
          <input value={filters.prop_id} onChange={(e) => onFilterChange('prop_id', e.target.value)} placeholder="PO69..." />
        </div>
        <div className="eod-filter-group">
          <label>Agent</label>
          <select value={filters.agent_id} onChange={(e) => onFilterChange('agent_id', e.target.value)}>
            <option value="">ทั้งหมด</option>
            {agents.map((a) => (
              <option key={a.agent_id} value={a.agent_id}>
                {a.agent_id}{a.agent_name && a.agent_name !== a.agent_id ? ` — ${a.agent_name}` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="eod-filter-group">
          <label>APFS Payment</label>
          <select value={filters.matched} onChange={(e) => onFilterChange('matched', e.target.value)}>
            <option value="">ทั้งหมด</option>
            <option value="yes">Paid (จับคู่ได้)</option>
            <option value="no">ไม่พบคู่</option>
          </select>
        </div>
        <div className="eod-filter-group">
          <label>Confirm จาก</label>
          <input type="date" value={filters.date_from} onChange={(e) => onFilterChange('date_from', e.target.value)} />
        </div>
        <div className="eod-filter-group">
          <label>Confirm ถึง</label>
          <input type="date" value={filters.date_to} onChange={(e) => onFilterChange('date_to', e.target.value)} />
        </div>
        <div className="eod-filter-actions">
          <button className="eod-btn-search" onClick={() => fetchRows(1)}>ค้นหา</button>
          <button className="eod-btn-clear" onClick={() => { clearFilters(); setTimeout(() => fetchRows(1), 0); }}>ล้าง</button>
        </div>
      </div>

      {/* Summary — totals are over the WHOLE filtered set, not just this page */}
      <div className="eod-stats">
        <div className="eod-stat"><span>ทั้งหมด (records)</span><b>{totals?.count ?? pagination.total}</b></div>
        <div className="eod-stat eod-stat-green"><span>Paid (หน้านี้)</span><b>{paidCount}</b></div>
        <div className="eod-stat"><span>Gross Premium</span><b>฿{fmtMoney(totals?.trans_gross_premium)}</b></div>
        <div className="eod-stat"><span>After Discount</span><b>฿{fmtMoney(totals?.trans_gross_premium_after_discount)}</b></div>
        <div className="eod-stat eod-stat-green"><span>Transfer ({transferPct}%)</span><b>฿{fmtMoney(totals?.transfer)}</b></div>
      </div>

      {error && <div className="eod-error"><span>⚠️</span>{error}</div>}

      <div className="eod-table-wrapper">
        <table className="eod-table">
          <thead>
            <tr>
              <th>APFS_Payment</th>
              <th>Prop ID</th>
              <th>Charge ID</th>
              <th>Confirm Status</th>
              <th>Confirm Date</th>
              <th>Customer</th>
              <th>Program</th>
              <th>Agent</th>
              <th className="text-right">Gross Premium</th>
              <th className="text-right">Fee_All</th>
              <th className="text-right">After Discount</th>
              <th className="text-right">Transfer ({transferPct}%)</th>
              <th className="text-right">Installment</th>
              <th className="text-right">Outstanding</th>
              <th className="text-right">Down Payment</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={15} className="eod-empty">กำลังโหลด...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={15} className="eod-empty">ไม่มีข้อมูล — อัปโหลดไฟล์ EOD ด้านบนเพื่อเริ่มต้น</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className={r.apfs_matched ? 'eod-row-paid' : ''}>
                <td>
                  <span className={`eod-badge ${r.apfs_matched ? 'eod-badge-paid' : 'eod-badge-none'}`}>
                    {r.apfs_payment}
                  </span>
                </td>
                <td className="font-mono">{r.prop_id}</td>
                <td className="font-mono eod-truncate" title={r.charge_id}>{r.charge_id || '-'}</td>
                <td>{r.confirm_status || '-'}</td>
                <td>{fmtDate(r.confirm_date)}</td>
                <td>{[r.p_holder_first_name, r.p_holder_last_name].filter(Boolean).join(' ') || '-'}</td>
                <td><small>{r.program_name || '-'}</small></td>
                <td><small>{r.agent_id || '-'}{r.agent_name && r.agent_name !== r.agent_id ? ` (${r.agent_name})` : ''}</small></td>
                <td className="text-right">฿{fmtMoney(r.trans_gross_premium)}</td>
                <td className="text-right">฿{fmtMoney(r.fee_all)}</td>
                <td className="text-right">฿{fmtMoney(r.trans_gross_premium_after_discount)}</td>
                <td className="text-right eod-transfer">฿{fmtMoney(r.transfer)}</td>
                <td className="text-right">฿{fmtMoney(r.installment_value)}</td>
                <td className="text-right">฿{fmtMoney(r.outstanding_balance)}</td>
                <td className="text-right">฿{fmtMoney(r.customer_downpayment_all)}</td>
              </tr>
            ))}
          </tbody>
          {totals && rows.length > 0 && (
            <tfoot>
              <tr className="eod-totals-row">
                <td colSpan={8} className="text-right">รวมทั้งหมด (filtered): {totals.count} รายการ</td>
                <td className="text-right">฿{fmtMoney(totals.trans_gross_premium)}</td>
                <td className="text-right">฿{fmtMoney(totals.fee_all)}</td>
                <td className="text-right">฿{fmtMoney(totals.trans_gross_premium_after_discount)}</td>
                <td className="text-right eod-transfer">฿{fmtMoney(totals.transfer)}</td>
                <td className="text-right">฿{fmtMoney(totals.installment_value)}</td>
                <td className="text-right">฿{fmtMoney(totals.outstanding_balance)}</td>
                <td className="text-right">฿{fmtMoney(totals.customer_downpayment_all)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="eod-pagination">
          <button disabled={pagination.page <= 1} onClick={() => fetchRows(pagination.page - 1)}>← ก่อนหน้า</button>
          <span>หน้า {pagination.page} / {pagination.totalPages}</span>
          <button disabled={pagination.page >= pagination.totalPages} onClick={() => fetchRows(pagination.page + 1)}>ถัดไป →</button>
        </div>
      )}
    </div>
  );
}
